import { LazyRouteOutlet } from '@/components/routes/LazyRouteOutlet';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Sidebar } from '@/components/layout/Sidebar';
import { SidebarEdgeToggle } from '@/components/layout/SidebarEdgeToggle';
import { AppLauncher } from '@/components/layout/AppLauncher';
import { CompanySwitcher } from '@/components/layout/CompanySwitcher';
import { PosStoreSelector } from '@/components/pos/PosStoreSelector';
import { CommandPalette } from '@/components/layout/CommandPalette';
import { usePermissions } from '@/hooks/usePermissions';
import { useFeatures } from '@/core/features/useFeatures';
import { useActiveModule } from '@/hooks/useActiveModule';
import { useModuleAccessRecovery } from '@/hooks/useModuleAccessRecovery';
import { usePermissionDeniedRecovery } from '@/hooks/usePermissionDeniedRecovery';
import { useLauncherModuleSync } from '@/hooks/useLauncherModuleSync';
import { getAppNavConfig } from '@/config/appNavigation';
import {
  composeWorkspaceNavGroups,
  filterNavGroupsByAccess,
  getGlobalWorkNavGroups,
  mergeBackendWithCanonicalNav,
} from '@/utils/navigation';
import { useBackendNavigation } from '@/hooks/useBackendNavigation';
import { isRouteAllowedForEntryModule } from '@/utils/moduleScope';
import { getModuleEntryByErpModule } from '@/config/moduleEntries';
import { localizeModuleLabel } from '@/i18n/navLabel';
import { resolveModuleFromPath } from '@/config/appNavigation';
import { isPlatformAdminRoute } from '@/config/moduleDefaults';
import { resolveSafeRevertPath } from '@/utils/routeAccess';
import { readRecentPaths, recordRecentPath, resolveQuickAccessItems } from '@/utils/quickAccess';
import { readFavoritePaths, resolveNavItemsByPaths, toggleFavoritePath } from '@/utils/navFavorites';
import { notify } from '@/core/toast/notify';
import { resolveAccessibleLandingPath } from '@/utils/pageAccessEngine';
import { isOrgModuleSubscribed, isStaleModulePath, workspaceModuleHome } from '@/utils/moduleNavigation';
import { shouldShowAppLauncher } from '@/utils/moduleAccess';
import { setWorkspaceModule } from '@/store/authSlice';
import { toggleSidebar } from '@/store/appSlice';
import { refreshSessionInit } from '@/store/sessionSlice';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import { Button } from '@/components/ui/Button';
import { useWorkspaceTitle } from '@/hooks/useWorkspaceTitle';
import { useCompanyScopeQuerySync } from '@/hooks/useCompanyScopeQuerySync';
import { TourProvider } from '@/core/tour/TourProvider';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { prefetchRoute } from '@/utils/routePrefetch';

export function OrgAdminLayout() {
  const { t } = useTranslation('org');
  useWorkspaceTitle();
  useCompanyScopeQuerySync();
  const activeModule = useActiveModule();
  const { hasFeature, refresh: refreshFeatures } = useFeatures();
  const navModulesVersion = useAppSelector((s) => s.app.navModulesVersion);
  const initialNavVersion = useRef(navModulesVersion);
  const enabledModules = useAppSelector((s) => s.auth.enabledModules);
  const accessibleModules = useAppSelector((s) => s.auth.accessibleModules);
  const { has, permissions } = usePermissions();
  const roleCode = useAppSelector((s) => s.auth.user?.roleCode);
  const authLoading = useAppSelector((s) => s.auth.loading);
  const { canAccessPath } = useModuleAccessRecovery(activeModule);
  usePermissionDeniedRecovery(activeModule);
  useLauncherModuleSync();

  const moduleEntry = activeModule ? getModuleEntryByErpModule(activeModule) : undefined;
  const navConfig = activeModule ? getAppNavConfig(activeModule) : undefined;
  const { t: tNav } = useTranslation('navigation');
  const appLabel = activeModule
    ? localizeModuleLabel(activeModule, tNav, navConfig?.label ?? moduleEntry?.label)
    : t('pages.administration', { defaultValue: 'Workspace' });
  const appSubtitle = navConfig?.subtitle;
  const [searchOpen, setSearchOpen] = useState(false);
  const [favTick, setFavTick] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const backendNav = useBackendNavigation(activeModule);
  const screenActions = useAppSelector((s) => s.session.screenActions);
  const sessionLoaded = useAppSelector((s) => s.session.loaded);
  const screenGrantsConfigured = useAppSelector((s) => s.session.screenGrantsConfigured);

  const workflowGroups = useMemo(() => {
    const globalGroups = activeModule
      ? getGlobalWorkNavGroups(
          activeModule,
          has,
          hasFeature,
          permissions,
          screenActions,
          roleCode,
          sessionLoaded,
          screenGrantsConfigured,
        )
      : [];
    const moduleNav = activeModule
      ? mergeBackendWithCanonicalNav(
          backendNav,
          activeModule,
          has,
          permissions,
          hasFeature,
          roleCode,
          screenActions,
          sessionLoaded,
          screenGrantsConfigured,
        )
      : filterNavGroupsByAccess(
          backendNav,
          screenActions,
          has,
          permissions,
          hasFeature,
          roleCode,
          sessionLoaded,
          screenGrantsConfigured,
        );
    return composeWorkspaceNavGroups(moduleNav, globalGroups, activeModule ?? undefined);
  }, [
    activeModule,
    backendNav,
    has,
    hasFeature,
    permissions,
    roleCode,
    screenActions,
    sessionLoaded,
    screenGrantsConfigured,
  ]);

  const allNavItems = useMemo(
    () => workflowGroups.flatMap((group) => group.items),
    [workflowGroups],
  );

  const recentItems = useMemo(
    () => resolveQuickAccessItems(readRecentPaths(), allNavItems),
    [allNavItems, location.pathname],
  );

  const favoriteItems = useMemo(
    () => resolveNavItemsByPaths(readFavoritePaths(), allNavItems),
    [allNavItems, location.pathname, favTick],
  );

  const openSearch = useCallback(() => setSearchOpen(true), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (activeModule === 'HR') {
      prefetchRoute('/hr/attendance');
    }
  }, [activeModule]);

  const collapsed = useAppSelector((s) => s.app.sidebarCollapsed);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (navModulesVersion === initialNavVersion.current) return;
    void refreshFeatures();
    void dispatch(refreshSessionInit(undefined));
  }, [navModulesVersion, refreshFeatures, dispatch]);

  useEffect(() => {
    if (isPlatformAdminRoute(location.pathname)) return;
    if (authLoading) return;
    if (!activeModule) return;

    if (!canAccessPath(location.pathname)) {
      const landing = resolveAccessibleLandingPath(
        {
          permissions,
          roleCode,
          enabledModules,
          accessibleModules,
          entryModule: activeModule,
          hasPermission: has,
        },
        activeModule,
      );
      const current = `${location.pathname}${location.search}`;
      if (landing && landing !== current) {
        const module = resolveModuleFromPath(landing.split('?')[0]);
        if (module) {
          dispatch(setWorkspaceModule(module));
        }
        navigate(landing, { replace: true });
      }
      return;
    }

    if (isRouteAllowedForEntryModule(location.pathname, activeModule)) return;
    if (!isOrgModuleSubscribed(activeModule, enabledModules, accessibleModules)) return;

    const home = workspaceModuleHome(activeModule);

    if (isStaleModulePath(location.pathname, activeModule)) {
      const current = `${location.pathname}${location.search}`;
      if (home !== current && home !== location.pathname) {
        navigate(home, { replace: true });
      }
      return;
    }

    notify.error(t('errors.thisPageIsNotAvailableInYourCurrent'));
    const revertTo = resolveSafeRevertPath(location.pathname, home, { canAccess: canAccessPath });
    if (revertTo && revertTo !== `${location.pathname}${location.search}`) {
      navigate(revertTo, { replace: true });
    }
  }, [
    activeModule,
    accessibleModules,
    authLoading,
    canAccessPath,
    dispatch,
    enabledModules,
    has,
    location.pathname,
    location.search,
    navigate,
    permissions,
    roleCode,
  ]);

  useEffect(() => {
    const allItems = workflowGroups.flatMap((group) => group.items);
    const match = allItems.find(
      (item) =>
        location.pathname === item.to.split('?')[0] ||
        (item.to.split('?')[0] !== '/pos/dashboard' &&
          location.pathname.startsWith(`${item.to.split('?')[0]}/`)),
    );
    if (!match) return;
    recordRecentPath(match.to, readRecentPaths());
  }, [location.pathname, workflowGroups]);

  const posShortcut =
    activeModule === 'POS' && has('BILLING_CREATE') ? (
      <Button variant="secondary" className="hidden lg:inline-flex" onClick={() => navigate('/pos')}>
        <PointOfSaleIcon fontSize="small" />
        Open POS
      </Button>
    ) : null;

  const pinCurrentPage = () => {
    toggleFavoritePath(location.pathname);
    setFavTick((t) => t + 1);
    notify.t.success('toast.pagePinnedToFavorites', { ns: 'org' });
  };

  const showAppLauncher =
    Boolean(activeModule) &&
    shouldShowAppLauncher(enabledModules, permissions, accessibleModules, roleCode, has);

  return (
    <TourProvider module={activeModule ?? null}>
    <div className="workspace-shell flex h-screen flex-col bg-page dark:bg-slate-950">
      <Header
        moduleLabel={activeModule ?? appLabel}
        trailing={
          <>
            <CompanySwitcher className="w-full" />
            {(activeModule === 'POS' || activeModule === 'INVENTORY' || activeModule === 'WAREHOUSE') && (
              <PosStoreSelector className="hidden w-full sm:block" />
            )}
          </>
        }
        onOpenSearch={openSearch}
        extra={
          <>
            <Button
              type="button"
              variant="ghost"
              className="workspace-header-extra-btn"
              onClick={pinCurrentPage}
              title={t('pages.pinPageToFavorites')}
            >
              <StarBorderIcon fontSize="small" />
            </Button>
            {posShortcut}
          </>
        }
      />
      <div className="relative flex min-h-0 flex-1 overflow-hidden">
      {!collapsed && (
        <button
          type="button"
          className="workspace-sidebar-backdrop"
          aria-label={t('labels.closeSidebar')}
          onClick={() => dispatch(toggleSidebar())}
        />
      )}
      <Sidebar
        workflowGroups={workflowGroups}
        recentItems={recentItems}
        favoriteItems={favoriteItems}
        collapsed={collapsed}
        onToggle={() => dispatch(toggleSidebar())}
        appLabel={appSubtitle}
        brand={appLabel}
        accent="violet"
        hasFeature={hasFeature}
        footer={
          showAppLauncher && activeModule ? (
            <AppLauncher
              activeModule={activeModule}
              variant="sidebar"
              collapsed={collapsed}
            />
          ) : null
        }
      />
      {collapsed && (
        <SidebarEdgeToggle
          collapsed
          onToggle={() => dispatch(toggleSidebar())}
          variant="mobile"
        />
      )}
      <main id="main-content" className="flex-1 overflow-y-auto p-6" role="main">
        <LazyRouteOutlet />
      </main>
      </div>
      <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
    </TourProvider>
  );
}
